import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import { toast } from '@multaqa/ui/components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import type { TemplateDepartment, TemplateItemInput, TemplatePosition } from './api'
import { DeleteDialog, TemplateDialog } from './group-defaults-template-dialogs'

export type TemplateKind = 'departments' | 'positions'
export type TemplateItem = TemplateDepartment | TemplatePosition
export type TemplateActions = {
  pending: boolean
  create: (input: TemplateItemInput) => Promise<unknown>
  update: (id: string, input: TemplateItemInput) => Promise<unknown>
  remove: (id: string) => Promise<unknown>
}

export function TemplateTable({
  kind,
  items,
  loading,
  error,
  retry,
  actions,
  systemDefaults,
}: {
  kind: TemplateKind
  items: TemplateItem[]
  loading: boolean
  error: Error | null
  retry: () => unknown
  actions: TemplateActions
  systemDefaults: readonly TemplateItemInput[]
}) {
  const { t } = useTranslation()
  const messages = useAppErrorMessages()
  const [editing, setEditing] = useState<TemplateItem | 'new' | null>(null)
  const [deleting, setDeleting] = useState<TemplateItem | null>(null)
  const [loadingSystem, setLoadingSystem] = useState(false)
  const loadSystem = async () => {
    setLoadingSystem(true)
    try {
      await Promise.all(systemDefaults.map((item) => actions.create(item)))
      toast.success(t('groups.defaults.templates.loaded'))
    } catch (cause) {
      toast.error(errorMessage(cause, t))
    } finally {
      setLoadingSystem(false)
    }
  }
  if (loading)
    return (
      <Panel data-testid={`group-template-${kind}-loading`}>
        <PanelContent className="p-4">
          <ListSkeleton columns={[2, 2, 1, 0.5]} rows={5} />
        </PanelContent>
      </Panel>
    )
  if (error) return <AppError error={error} messages={messages} retry={retry} />
  return (
    <Panel data-testid={`group-template-${kind}`}>
      <PanelHeader>
        <PanelTitle>{t(`groups.defaults.templates.${kind}.title`)}</PanelTitle>
        <Button
          type="button"
          size="sm"
          className="ms-auto"
          onClick={() => setEditing('new')}
          data-testid={`group-template-${kind}-add`}
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
          {t('groups.defaults.templates.add')}
        </Button>
      </PanelHeader>
      <PanelContent>
        {items.length === 0 ? (
          <EmptyState
            title={t(`groups.defaults.templates.${kind}.emptyTitle`)}
            description={t(`groups.defaults.templates.${kind}.emptyBody`)}
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={loadingSystem}
                  disabled={loadingSystem || actions.pending}
                  onClick={() => void loadSystem()}
                  data-testid={`group-template-${kind}-load-system`}
                >
                  {t('groups.defaults.templates.loadSystem')}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setEditing('new')}>
                  {t('groups.defaults.templates.add')}
                </Button>
              </div>
            }
          />
        ) : (
          <Table className="border border-border" data-testid={`group-template-${kind}-table`}>
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead>{t('groups.defaults.templates.columns.nameEn')}</TableHead>
                <TableHead>{t('groups.defaults.templates.columns.nameAr')}</TableHead>
                <TableHead>{t('groups.defaults.templates.columns.code')}</TableHead>
                <TableHead className="text-end">
                  {t('groups.defaults.templates.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TemplateRow
                  key={item.id}
                  kind={kind}
                  item={item}
                  onEdit={() => setEditing(item)}
                  onDelete={() => setDeleting(item)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </PanelContent>
      {editing !== null ? (
        <TemplateDialog
          key={editing === 'new' ? 'new' : editing.id}
          kind={kind}
          item={editing === 'new' ? null : editing}
          actions={actions}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <DeleteDialog
        kind={kind}
        item={deleting}
        actions={actions}
        onClose={() => setDeleting(null)}
      />
    </Panel>
  )
}

function TemplateRow({
  kind,
  item,
  onEdit,
  onDelete,
}: {
  kind: TemplateKind
  item: TemplateItem
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  return (
    <TableRow data-testid={`group-template-${kind}-row-${item.id}`}>
      <TableCell>{item.name.en ?? ''}</TableCell>
      <TableCell dir="rtl">{item.name.ar ?? ''}</TableCell>
      <TableCell className="font-mono text-identifier">{item.code}</TableCell>
      <TableCell className="text-end">
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`${t('groups.defaults.templates.edit')} ${item.code}`}
            onClick={onEdit}
            data-testid={`group-template-${kind}-edit-${item.id}`}
          >
            <PencilIcon className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`${t('groups.defaults.templates.deleteAction')} ${item.code}`}
            onClick={onDelete}
            data-testid={`group-template-${kind}-delete-${item.id}`}
          >
            <Trash2Icon className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function errorMessage(error: unknown, t: (key: string) => string): string {
  return formatErrorMessage(error, t('groups.defaults.errors.generic'))
}
